import {
  COLLECTION_SOURCE,
  INVOICE_STATUS,
  INVOICE_TYPE,
  IRightsType,
  MEDIA_TYPE,
  ORDER_STATUS,
  PAY_TYPE,
  PUBLISH_PLATFORM,
  RESOURCE_TYPE,
} from '../types/enums';

import { getI18nService } from '@rendora/common/services/i18n/index';

export type IEnumType =
  | 'rights_type'
  | 'pay_type'
  | 'order_status'
  | 'invoice_type'
  | 'invoice_status'
  | 'resource_type'
  | 'publish_platform'
  | 'media_type'
  | 'collection_source';

// 精确类型映射
type EnumMap = {
  rights_type: Record<IRightsType, string>;
  pay_type: Record<PAY_TYPE, string>;
  order_status: Record<ORDER_STATUS, string>;
  invoice_type: Record<INVOICE_TYPE, string>;
  invoice_status: Record<INVOICE_STATUS, string>;
  resource_type: Record<RESOURCE_TYPE, string>;
  publish_platform: Record<PUBLISH_PLATFORM, string>;
  media_type: Record<MEDIA_TYPE, string>;
  collection_source: Record<COLLECTION_SOURCE, string>;
};

// 用于穷尽校验
function assertNever(value: never): never {
  // 这里抛错只是兜底，类型上不会走到这里
  throw new Error(`Unhandled key: ${String(value)}`);
}

// 重载：根据 key 推导出精确返回类型
export function useEnumDictionary(key: 'rights_type'): EnumMap['rights_type'];
export function useEnumDictionary(key: 'pay_type'): EnumMap['pay_type'];
export function useEnumDictionary(key: 'order_status'): EnumMap['order_status'];
export function useEnumDictionary(key: 'invoice_type'): EnumMap['invoice_type'];
export function useEnumDictionary(key: 'invoice_status'): EnumMap['invoice_status'];
export function useEnumDictionary(key: 'resource_type'): EnumMap['resource_type'];
export function useEnumDictionary(key: 'publish_platform'): EnumMap['publish_platform'];
export function useEnumDictionary(key: 'media_type'): EnumMap['media_type'];
export function useEnumDictionary(key: 'collection_source'): EnumMap['collection_source'];
export function useEnumDictionary(key: IEnumType): EnumMap[keyof EnumMap] {
  const i18nSrv = getI18nService();
  const $t = i18nSrv.t;

  // 权益类型映射
  const rightsTypeMap = {
    [IRightsType.UN_AUTHORIZED]: $t('wei-ren-zheng'),
    [IRightsType.TASTE]: $t('ti-yan-ban'),
    [IRightsType.PERSONAL_LITE]: $t('ge-ren-ru-men-ban'),
    [IRightsType.PERSONAL]: $t('ge-ren-ban-biao-zhun-ban'),
    [IRightsType.TEAM_TASTE]: $t('tuan-dui-ti-yan-ban'),
    [IRightsType.TEAM_LIGHT]: $t('tuan-dui-ru-men-ban'),
    [IRightsType.ORG_STANDARD]: $t('qi-ye-biao-zhun-ban'),
    [IRightsType.ORG_FLAGSHIP]: $t('qi-ye-qi-jian-ban'),
    [IRightsType.ORG_CUSTOM]: $t('qi-ye-ding-zhi'),
  };

  // 支付方式映射
  const payTypeMap = {
    [PAY_TYPE.NONE]: $t('wei-xuan-ze'),
    [PAY_TYPE.ALIPAY]: $t('zhi-fu-bao'),
    [PAY_TYPE.WEIXIN]: $t('wei-xin'),
    [PAY_TYPE.TRANSFER]: $t('dui-gong-zhuan-zhang'),
    [PAY_TYPE.FREE]: $t('wu-xu-fu-kuan'),
    [PAY_TYPE.AIRWALLEX]: 'airwallex',
    [PAY_TYPE.STRIPE]: 'stripe',
  };

  const orderStatusMap = {
    [ORDER_STATUS.UNPAID]: $t('wei-fu-kuan'),
    [ORDER_STATUS.PAID]: $t('yi-fu-kuan'),
    [ORDER_STATUS.CLOSE]: $t('jiao-yi-guan-bi'),
    [ORDER_STATUS.REFUND]: $t('yi-tui-kuan'),
    [ORDER_STATUS.FAIL]: $t('zhi-fu-shi-bai'),
    [ORDER_STATUS.REVOKE]: $t('yi-che-xiao'),
    [ORDER_STATUS.PAYING]: $t('zhi-fu-zhong'),
    [ORDER_STATUS.FINISHED]: $t('jiao-yi-jie-shu'),
    [ORDER_STATUS.EXPIRED]: $t('yi-guo-qi'),
  };

  const invoiceTypeMap = {
    [INVOICE_TYPE.NORMAL]: $t('pu-tong-fa-piao'),
    [INVOICE_TYPE.VAT]: $t('zeng-zhi-shui-zhuan-yong-fa-piao'),
  };

  const invoiceStatusMap = {
    [INVOICE_STATUS.SUCCESS]: $t('kai-piao-wan-cheng'),
    [INVOICE_STATUS.FAIL]: $t('kai-piao-shi-bai'),
    [INVOICE_STATUS.WAIT]: $t('kai-piao-zhong'),
  };

  const resourceTypeMap = {
    [RESOURCE_TYPE.TTS_VCN]: $t('yin-se'),
    [RESOURCE_TYPE.MAP]: $t('chang-jing'),
    [RESOURCE_TYPE.CHARACTER]: $t('ren-wu'),
    [RESOURCE_TYPE.FONT]: $t('zi-ti'),
    [RESOURCE_TYPE.MATERIAL]: $t('su-cai'),
    [RESOURCE_TYPE.MUSIC]: $t('yin-le'),
    [RESOURCE_TYPE.AUDIO]: $t('yin-xiao'),
    [RESOURCE_TYPE.TEMPLATE]: $t('mo-ban'),
    [RESOURCE_TYPE.PASTER]: $t('tie-zhi'),
    [RESOURCE_TYPE.AE_PAG_TEMPLATE_HEAD]: $t('pian-tou'),
    [RESOURCE_TYPE.AE_PAG_TEMPLATE_TAIL]: $t('pian-wei'),
    [RESOURCE_TYPE.COLLECTION_LOGO]: $t('tao-zhuang-logo-tu'),
    [RESOURCE_TYPE.COLLECTION]: $t('tao-zhuang'),
    [RESOURCE_TYPE.HAIR_COLLECTION]: $t('tou-fa'),
    [RESOURCE_TYPE.MAKEUP_COLLECTION]: $t('zhuang-rong'),
    [RESOURCE_TYPE.ACCESSORY]: $t('shi-pin-0'),
    [RESOURCE_TYPE.MEME]: $t('biao-qing-bao'),
    [RESOURCE_TYPE.THEME_LAYER]: $t('zhu-ti-su-cai'),
    [RESOURCE_TYPE.SKU]: 'sku',
    [RESOURCE_TYPE.PPT_MEDIA]: $t('ppt-su-cai'),
    [RESOURCE_TYPE.PPT_TEMPLATE]: $t('ppt-mo-ban'),
    [RESOURCE_TYPE.POSTER_TEMPLATE]: $t('jiao-se-hai-bao'),
    [RESOURCE_TYPE.MEDIA_BOX]: $t('su-cai-ku'),
  };

  const publishPlatformMap = {
    [PUBLISH_PLATFORM.DOUYIN]: $t('dou-yin'),
    [PUBLISH_PLATFORM.KUAISHOU]: $t('kuai-shou'),
    [PUBLISH_PLATFORM.BLIBLI]: $t('b-zhan'),
    [PUBLISH_PLATFORM.XHS]: $t('xiao-hong-shu'),
    [PUBLISH_PLATFORM.TIKTOK]: 'tiktok',
    [PUBLISH_PLATFORM.YOUTUBE]: 'youtube',
    [PUBLISH_PLATFORM.EMAIL]: 'email',
  };

  const mediaTypeMap = {
    [MEDIA_TYPE.IMAGE]: $t('tu-pian'),
    [MEDIA_TYPE.VIDEO]: $t('shi-pin'),
    [MEDIA_TYPE.AUDIO]: $t('yin-pin'),
    [MEDIA_TYPE.PPT]: 'PPT',
    [MEDIA_TYPE.PDF]: 'PDF',
    [MEDIA_TYPE.DOCX]: 'Word',
    [MEDIA_TYPE.FONT]: $t('zi-ti'),
    [MEDIA_TYPE.PAG]: 'PAG',
    [MEDIA_TYPE.BACKGROUND_IMAGE]: $t('bei-jing-tu'),
  };

  const collectionSourceMap = {
    [COLLECTION_SOURCE.PERSON]: $t('ge-ren-kong-jian'),
    [COLLECTION_SOURCE.TEAM]: $t('tuan-dui-kong-jian'),
    [COLLECTION_SOURCE.SHARE]: $t('yu-wo-fen-xiang'),
    [COLLECTION_SOURCE.YOUYAN]: $t('you-yan-ping-tai'),
    [COLLECTION_SOURCE.RECENTLY]: $t('zui-jin-zuo-pin'),
  };

  switch (key) {
    case 'rights_type':
      return rightsTypeMap;
    case 'pay_type':
      return payTypeMap;
    case 'order_status':
      return orderStatusMap;
    case 'invoice_type':
      return invoiceTypeMap;
    case 'invoice_status':
      return invoiceStatusMap;
    case 'resource_type':
      return resourceTypeMap;
    case 'publish_platform':
      return publishPlatformMap;
    case 'media_type':
      return mediaTypeMap;
    case 'collection_source':
      return collectionSourceMap;
    default:
      return assertNever(key);
  }
}
